<template>
  <div class="bulk-action-bar" v-if="selectedDays.length">
    <div class="bulk-action-bar__selection">
      <span class="bulk-action-bar__count">
        <i class="fal fa-check-square mr-2"></i>{{ countLabel }}
      </span>
      <a href="#" class="bulk-action-bar__clear" @click.prevent="$emit('clear')">
        <i class="fa fa-times mr-1"></i><span class="bulk-action-bar__label">{{ trans.clear_selection }}</span>
      </a>
    </div>

    <div class="bulk-action-bar__actions">
      <button type="button" class="btn btn-light" v-if="canUnlock" @click="$emit('unlock')">
        <i class="fas fa-unlock text-warning mr-2"></i><span class="bulk-action-bar__label">{{ trans.unlock }}</span>
      </button>
      <button type="button" class="btn btn-light" v-if="canLock" @click="$emit('lock')">
        <i class="fas fa-lock text-success mr-2"></i><span class="bulk-action-bar__label">{{ trans.lock }}</span>
      </button>

      <div v-if="fillOptions.length" class="btn-group bulk-action-bar__split bulk-action-bar__split--primary">
        <button type="button" class="btn btn-primary" @click="$emit('fill', fillOptions[0].mode)">
          <i class="mr-2" :class="fillOptions[0].icon"></i><span
          class="bulk-action-bar__label">{{ fillOptions[0].label }}</span>
        </button>
        <template v-if="fillOptions.length > 1">
          <button type="button" class="btn btn-primary bulk-action-bar__caret"
                  @click="toggleMenu('fill')" :aria-expanded="openMenu === 'fill' ? 'true' : 'false'">
            <i class="fas fa-caret-down"></i>
          </button>
          <div class="bulk-action-bar__menu" v-if="openMenu === 'fill'">
            <a v-for="option in fillOptions.slice(1)" :key="option.mode" href="#"
               @click.prevent="choose('fill', option.mode)">
              <i class="fa-fw mr-2" :class="option.icon"></i>{{ option.label }}
            </a>
          </div>
        </template>
      </div>

      <div v-if="deleteOptions.length" class="btn-group bulk-action-bar__split bulk-action-bar__split--danger">
        <button type="button" class="btn btn-danger" @click="$emit('delete', deleteOptions[0].mode)">
          <i class="far fa-trash-alt mr-2"></i><span
          class="bulk-action-bar__label">{{ deleteOptions[0].label }}</span>
        </button>
        <template v-if="deleteOptions.length > 1">
          <button type="button" class="btn btn-danger bulk-action-bar__caret"
                  @click="toggleMenu('delete')" :aria-expanded="openMenu === 'delete' ? 'true' : 'false'">
            <i class="fas fa-caret-down"></i>
          </button>
          <div class="bulk-action-bar__menu" v-if="openMenu === 'delete'">
            <a v-for="option in deleteOptions.slice(1)" :key="option.mode" href="#"
               @click.prevent="choose('delete', option.mode)">
              <i class="fa-fw mr-2" :class="option.icon"></i>{{ option.label }}
            </a>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {pluralize} from '../../utils';

export default {
  name: 'bulk-action-bar',

  props: {
    'selectedDays': {
      type: Array,
      required: true
    },
    'canLock': {
      type: Boolean,
      default: true
    },
    'canUnlock': {
      type: Boolean,
      default: true
    },
    'canFillReality': {
      type: Boolean,
      default: true
    },
    'canFillPlan': {
      type: Boolean,
      default: true
    },
    'canDeleteReality': {
      type: Boolean,
      default: true
    },
    'canDeletePlan': {
      type: Boolean,
      default: true
    },
    'trans': {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      openMenu: null
    }
  },

  computed: {
    countLabel() {
      return pluralize(this.trans.selected, this.selectedDays.length);
    },

    fillOptions() {
      return [
        this.canFillReality ? {mode: 'reality', label: this.trans.fill_reality, icon: 'fal fa-clock'} : null,
        this.canFillPlan ? {mode: 'plan', label: this.trans.fill_plan, icon: 'fal fa-calendar-alt'} : null
      ].filter(Boolean);
    },

    deleteOptions() {
      return [
        this.canDeleteReality ? {mode: 'reality', label: this.trans.delete_reality, icon: 'fal fa-clock'} : null,
        this.canDeletePlan ? {mode: 'plan', label: this.trans.delete_plan, icon: 'fal fa-calendar-alt'} : null
      ].filter(Boolean);
    }
  },

  mounted() {
    document.addEventListener('click', this.closeMenuOnOutsideClick);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closeMenuOnOutsideClick);
  },

  methods: {
    toggleMenu(menu) {
      this.openMenu = this.openMenu === menu ? null : menu;
    },

    choose(action, mode) {
      this.openMenu = null;
      this.$emit(action, mode);
    },

    closeMenuOnOutsideClick(event) {
      if (this.openMenu && !this.$el.contains(event.target)) {
        this.openMenu = null;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.bulk-action-bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: nowrap;
  padding: 12px 18px;
  margin: 0;
  background: #dfe8ff;
  border: none;
  border-top: 2px solid rgba(66, 115, 250, 0.55);
  border-radius: 0 0 calc(0.25rem - 1px) calc(0.25rem - 1px);
  box-shadow: 0 -4px 16px rgba(45, 74, 138, 0.16);
}

.bulk-action-bar__selection {
  display: flex;
  align-items: center;
  gap: 16px;
  white-space: nowrap;
}

.bulk-action-bar__count {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 700;
  color: #22417d;

  i {
    font-size: 17px;
  }
}

.bulk-action-bar__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
}

.bulk-action-bar__actions .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 12px;
  font-weight: 600;
  line-height: 1;
}

.bulk-action-bar__actions .btn-light {
  color: #212529;
  background-color: #fff;
  border-color: #ced4da;

  &:hover,
  &:focus,
  &:active,
  &:not(:disabled):not(.disabled):active {
    color: #212529;
    background-color: #f1f3f5;
    border-color: #adb5bd;
  }
}

.bulk-action-bar__split {
  position: relative;
}

.bulk-action-bar__caret {
  padding: 0 9px;
  font-size: 12px;
}

.bulk-action-bar__menu {
  position: absolute;
  right: 0;
  bottom: 100%;
  z-index: 30;
  min-width: 100%;
  padding: 4px;
  border-radius: 0.25rem 0.25rem 0 0;
  box-shadow: 0 4px 14px rgba(45, 74, 138, 0.25);

  a {
    display: block;
    padding: 6px 10px;
    border-radius: 0.2rem;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      background: rgba(0, 0, 0, 0.18);
      color: #fff;
    }
  }
}

.bulk-action-bar__split--primary {
  .bulk-action-bar__caret {
    background-color: #5c86fb;
    border-color: #5c86fb;
  }

  .bulk-action-bar__menu {
    background: #4273FA;
  }
}

.bulk-action-bar__split--danger {
  .bulk-action-bar__caret {
    background-color: #fe6a83;
    border-color: #fe6a83;
  }

  .bulk-action-bar__menu {
    background: #FE3F61;
  }
}

.bulk-action-bar__clear {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  color: #e02b2b;
  text-decoration: none;

  &:hover,
  &:focus {
    color: #a71d1d;
    text-decoration: underline;
    outline: none;
  }
}

@media (max-width: 767.98px) {
  .bulk-action-bar {
    padding: 8px;
    gap: 8px;
  }

  .bulk-action-bar__label {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// resources/assets/admin/js/attendance/show/BulkActionBar.vue